import { useMemo } from 'react';

import {
  V1GenerationErrorKind,
  type V1Event,
  type V1GenerationFailed,
} from '@/generated/hooks/rain/models';
import { useAutomationGetEvents } from '@/generated/hooks/rain/v1/automation_scoped/thread_service';
import { RAIN_QUILL_AGENT_ID } from '@/modules/rain/agents/quill';
import {
  getEventTaskScope,
  ROOT_TASK_SCOPE,
} from '@/modules/rain/utils/events';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

/**
 * Where a Quill V2 draft sits in the blueprint → code lifecycle.
 *
 * - `unknown`: the log hasn't been read yet, so no phase can be claimed. Not a
 *   lifecycle position — callers that act on the build (offering another run)
 *   must wait, callers that merely reflect it can treat it as `blueprint`.
 * - `blueprint`: editable blueprint, "Approve & Build" offered.
 * - `building`: a build was asked for and no code has landed yet.
 * - `stopped`: the user stopped the build before it wrote any code.
 * - `failed`: the build ended on an error before it wrote any code.
 * - `built`: the draft carries code; the blueprint stage is over.
 */
export type TBlueprintBuildPhase =
  | 'unknown'
  | 'blueprint'
  | 'building'
  | 'stopped'
  | 'failed'
  | 'built';

/**
 * Matches the `<user_action type="make_deterministic">` wrapper the editor's
 * Approve & Build button sends. The tag is a frontend display convention, so
 * this recognises the button's own message rather than parsing agent intent.
 */
const BUILD_REQUEST_RE = /<user_action[^>]*\btype=["']make_deterministic["']/i;

const isBuildRequest = (event: V1Event): boolean =>
  BUILD_REQUEST_RE.test(event.input_text?.text ?? '');

/**
 * Events that prove the agent loop is alive, mirroring the list rain's own
 * generation state re-activates a turn on (see `generation-state.ts`).
 */
const isAgentActivity = (event: V1Event): boolean =>
  Boolean(
    event.output_text ||
      event.reasoning_text ||
      event.reasoning_summary ||
      event.tool_call_start ||
      event.tool_call_argument ||
      event.tool_result,
  );

/**
 * Reads the build phase off the thread's own event log.
 *
 * A build spans several quill turns, so it cannot be tracked as component
 * state: a reload would drop it while the build kept running server-side. The
 * log is the only record that survives, so the phase is derived from it.
 *
 * `building` runs from the build request until one of four things ends it:
 * code lands, the run ends without code, quill parks on a question, or the
 * user sends something else. The last two matter because the build request
 * invites clarifying questions — without them a parked build would read as
 * still-working forever.
 */
export const deriveBlueprintBuildPhase = (
  events: readonly V1Event[],
  hasBuiltCode: boolean,
): TBlueprintBuildPhase => {
  if (hasBuiltCode) return 'built';

  // Sub-agent task streams settle independently of the root loop, so only root
  // events speak for the build. Rolled-back events never happened.
  const rootEvents = events.filter(
    event => getEventTaskScope(event) === ROOT_TASK_SCOPE && !event.deleted,
  );

  let requestIndex = -1;
  for (let i = rootEvents.length - 1; i >= 0; i -= 1) {
    if (isBuildRequest(rootEvents[i])) {
      requestIndex = i;
      break;
    }
  }
  if (requestIndex === -1) return 'blueprint';

  const pendingInterrupts = new Set<string>();
  let endedWithout: V1GenerationFailed | undefined;
  for (const event of rootEvents.slice(requestIndex + 1)) {
    // The user said something of their own — they're driving now, not waiting.
    if (event.input_text) return 'blueprint';
    if (event.interrupt?.interrupt_id) {
      pendingInterrupts.add(event.interrupt.interrupt_id);
    }
    if (event.interrupt_answer?.interrupt_id) {
      pendingInterrupts.delete(event.interrupt_answer.interrupt_id);
    }
    // A retryable failure is auto-retry parking, not the end of the build —
    // same reading rain's own generation state takes.
    if (event.generation_failed && event.generation_failed.retryable !== true) {
      endedWithout = event.generation_failed;
    } else if (isAgentActivity(event)) {
      // The loop came back after that failure, so it wasn't the end after all.
      // Letting the last signal win keeps a resumed build from reading as
      // stopped, which would unlock the blueprint mid-write.
      endedWithout = undefined;
    }
  }

  // Parked on a question the user hasn't answered: the build isn't progressing,
  // so hand the blueprint and the composer back.
  if (pendingInterrupts.size > 0) return 'blueprint';

  // The build ended before writing any code. Kept distinct from `blueprint` so
  // the pane can say so and offer another run — reverting to a plain untouched
  // blueprint leaves no trace that a build was ever started.
  if (endedWithout) {
    return endedWithout.kind ===
      V1GenerationErrorKind.GENERATION_ERROR_KIND_STOPPED
      ? 'stopped'
      : 'failed';
  }

  return 'building';
};

export interface IUseBlueprintBuildPhaseParams {
  automationId: string;
  /** The draft's quill2 thread, or null while it's still resolving. */
  threadId: string | null;
  /** True once the draft carries code — ends the blueprint stage outright. */
  hasBuiltCode: boolean;
  /** Whether blueprint controls need the current build phase. */
  enabled: boolean;
}

/**
 * Shares the backlog query the chat already runs (identical key and params), so
 * mounting this observer fetches nothing on its own. It has to repeat the
 * chat's `refetchOnWindowFocus: false` though: that setting is per-observer, so
 * leaving it at React Query's default would refetch the whole log on every
 * window focus.
 *
 * The log is a snapshot — streamed events land in the message store, not this
 * cache — so whoever watches the stream has to invalidate this key when a turn
 * settles mid-build. `AutomationEditorChat` does that.
 */
export function useBlueprintBuildPhase({
  automationId,
  threadId,
  hasBuiltCode,
  enabled,
}: IUseBlueprintBuildPhaseParams): TBlueprintBuildPhase {
  const organization = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspace = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { data } = useAutomationGetEvents(
    organization,
    workspace,
    automationId,
    RAIN_QUILL_AGENT_ID,
    threadId ?? '',
    { compacted: false },
    {
      query: {
        enabled: enabled && Boolean(threadId),
        refetchOnWindowFocus: false,
      },
    },
  );

  const events = data?.data?.events;

  return useMemo(() => {
    if (hasBuiltCode) return 'built';
    // An inactive observer does not wait for the build log.
    if (!enabled) return 'blueprint';
    // Either the thread is still resolving or the log is still in flight.
    // Reporting `blueprint` here would offer another build while one runs.
    if (!events) return 'unknown';
    return deriveBlueprintBuildPhase(events, hasBuiltCode);
  }, [enabled, events, hasBuiltCode]);
}
